import type { BrowseArrival } from "@/lib/browse/browseArrival";

type EventProperties = Record<string, boolean | number | string>;

/** The one entry the Show identity keeps. Exported so the web store that holds it names it once. */
export const SHOW_ANALYTICS_KEY = "trovie.show-analytics";
const EEA_COUNTRIES = new Set([
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "ES",
  "FI",
  "FR",
  "GR",
  "HU",
  "IE",
  "IS",
  "IT",
  "LI",
  "LT",
  "LU",
  "LV",
  "MT",
  "NL",
  "NO",
  "PL",
  "PT",
  "RO",
  "SE",
  "SI",
  "SK",
]);

export type ShowAnalyticsSink = {
  capture(event: string, properties: EventProperties): void;
};

type SearchEvent =
  | { status: "unavailable"; showId: string; }
  | {
    status: "available";
    showId: string;
    resultCardCount: number;
    catalogCardId?: string;
    unresolvedQueryLength?: number;
    zeroResultCause?: "no_catalog_match" | "not_listed";
  };

/** What the item detail page can say about a card without naming it or pricing it. */
type BrowseItemFacts = {
  stockItemPublicId: string;
  hasPrice: boolean;
  hasComps: boolean;
};

/** A presented Pull Sheet's size, in the two units that differ when a customer takes duplicates. */
type PresentedSheetFacts = {
  /** Distinct units on the sheet. */
  lineCount: number;
  /** Copies across those units (Σ quantity). */
  itemCount: number;
};

export function createShowAnalytics(sink: ShowAnalyticsSink) {
  return {
    showOpened(showId: string, referrerClass: string, servedFromCache: boolean) {
      sink.capture("show_opened", {
        show_id: showId,
        referrer_class: referrerClass,
        served_from_cache: servedFromCache,
      });
    },
    showSearched(search: SearchEvent) {
      if (search.status === "unavailable") return;
      if (search.resultCardCount === 0 && !search.zeroResultCause) {
        throw new Error("Show analytics requires a zero-result cause");
      }

      sink.capture("show_searched", {
        show_id: search.showId,
        result_card_count: search.resultCardCount,
        ...(search.catalogCardId ? { catalog_card_id: search.catalogCardId } : {}),
        ...(search.unresolvedQueryLength === undefined
          ? {}
          : { unresolved_query_length: search.unresolvedQueryLength }),
        ...(search.zeroResultCause ? { zero_result_cause: search.zeroResultCause } : {}),
      });
    },
    cardOpened(showId: string, catalogCardId: string, vendorCount: number) {
      sink.capture("card_opened", {
        show_id: showId,
        catalog_card_id: catalogCardId,
        vendor_count: vendorCount,
      });
    },
    vendorOpened(showId: string, participationId: string, tableNumber: string) {
      sink.capture("vendor_opened", {
        show_id: showId,
        participation_id: participationId,
        table_number: tableNumber,
      });
    },
    /**
     * A visitor landed on a vendor's public browse page.
     *
     * `arrival` separates two audiences this event would otherwise average: visitors with no
     * referrer (what a scanned table QR looks like) and visitors who followed an Instagram or
     * Facebook link and never stood at the booth. They convert differently, so a single rate
     * across both describes nobody. See `browseArrival` for why the buckets are named after the
     * evidence rather than after the QR the event is named for.
     */
    tableQrOpened(eventPublicId: string, arrival: BrowseArrival) {
      sink.capture("table_qr_opened", { event_public_id: eventPublicId, arrival });
    },
    /**
     * The item detail view (TRO-642): the second step of the table QR funnel, and the first
     * signal that a scanned-in customer did anything at all.
     *
     * Fired from the screen rather than the browse list's tap handler, because this route is a
     * shareable link a customer can cold-launch straight into; counting it at the tap would miss
     * those and make the funnel look leakier than it is.
     *
     * `has_comps` and `has_price` say whether the page did its job for this card. A unit with
     * neither is the dead end TRO-483 added the TCGplayer link for, and these flags show how often
     * a customer hits one.
     */
    browseItemOpened(eventPublicId: string, item: BrowseItemFacts) {
      sink.capture("browse_item_opened", {
        event_public_id: eventPublicId,
        stock_item_public_id: item.stockItemPublicId,
        has_price: item.hasPrice,
        has_comps: item.hasComps,
      });
    },
    /**
     * The first pick: the write that creates the Pull Sheet, not every later one.
     *
     * This is the commitment moment: a customer who picks nothing only browsed, and a customer who
     * picks once has started a list they may hand over. Emitted once per sheet by the mutation
     * that created it, so a customer who resumes an existing sheet on the same device (ADR-0014)
     * does not re-enter the funnel.
     */
    pullSheetStarted(eventPublicId: string) {
      sink.capture("pull_sheet_started", { event_public_id: eventPublicId });
    },
    /**
     * Check out: the `open` → `presented` transition, when the list enters the vendor's queue.
     * The outcome the whole surface exists to produce.
     *
     * Carries two counts and nothing else: not the subtotal, `customer_label`, or item names. This
     * surface is anonymous by construction (ADR-0014, ADR-0076), and a sheet's money belongs to the
     * tenant, not to a product metric. `line_count` (distinct units) and `item_count` (copies,
     * Σ quantity) differ whenever a customer takes several of one card, so both are sent rather
     * than one ambiguous size.
     */
    pullSheetPresented(eventPublicId: string, sheet: PresentedSheetFacts) {
      sink.capture("pull_sheet_presented", {
        event_public_id: eventPublicId,
        line_count: sheet.lineCount,
        item_count: sheet.itemCount,
      });
    },
  };
}

/** The full anonymous Show and browse analytics surface, as returned by `createShowAnalytics`. */
export type ShowAnalytics = ReturnType<typeof createShowAnalytics>;

/**
 * Where the Show-scoped id lives: a site-scoped cookie, because the Show page and the table-QR page
 * are two origins (ADR-0106).
 *
 * Not a `Storage`, because a cookie has a lifetime and `localStorage` does not. `write` receives
 * the lifetime the id is allowed rather than choosing one, so no store can outlive its contents.
 * Every method may throw where a browser refuses cookies.
 */
export type ShowIdentityStore = {
  // Function properties, not method shorthand: the store is three closures over a cookie, with no
  // `this` to lose, and method syntax makes every `expect(store.write)` an unbound-method error.
  read: () => string | null;
  write: (value: string, maxAgeSeconds: number) => void;
  clear: () => void;
};

type ShowAnalyticsConfigInput = {
  country: string | null;
  /**
   * Null only where the page cannot name a Show: a table QR's URL carries the Event id and not the
   * Show id, so a browse payload that omits `show_public_id` leaves this null and reuses any saved
   * id. Pass the Show id wherever the payload does carry one (ADR-0106).
   */
  showId: string | null;
  showEndsAt: string | null;
  now: number;
  store: ShowIdentityStore;
  createId: () => string;
};

export function getShowAnalyticsConfig(input: ShowAnalyticsConfigInput) {
  const common = {
    autocapture: false,
    capture_pageleave: false,
    capture_pageview: false,
    disable_session_recording: true,
    person_profiles: "never",
  } as const;
  const memoryConfig = () => ({
    ...common,
    bootstrap: { distinctID: input.createId() },
    persistence: "memory" as const,
  });

  // Returns before `input.store` is touched at all. The store is a cookie, and finding out whether
  // its shared domain is accepted means writing a probe, so ADR-0061's promise that nothing is
  // written to an EEA visitor's device means bypassing the whole store, not skipping one key.
  if (!input.country || EEA_COUNTRIES.has(input.country.toUpperCase())) {
    return memoryConfig();
  }

  // Read here rather than after the Show-end branch, so that branch can tell whose id it holds.
  let saved: { expiresAt: number; id: string; showId: string; } | undefined;

  try {
    saved = JSON.parse(input.store.read() ?? "null") ?? undefined;
  } catch {
    // A malformed local value is simply replaced below.
  }

  const showEnd = input.showEndsAt ? Date.parse(input.showEndsAt) : Number.NaN;
  if (input.showId !== null && Number.isFinite(showEnd) && showEnd <= input.now) {
    try {
      // Only the entry this Show itself wrote. The store is one site-wide key (ADR-0106), so an
      // unconditional clear would let an ended Show's page delete a live Show's id mid-loop.
      if (saved?.showId === input.showId) input.store.clear();
    } catch {
      // Memory-only analytics still works when browser storage is unavailable.
    }
    return memoryConfig();
  }
  const fallbackExpiry = input.now + 48 * 60 * 60 * 1_000;
  const expiresAt = Number.isFinite(showEnd) && showEnd > input.now
    ? Math.min(showEnd, fallbackExpiry)
    : fallbackExpiry;

  const id = saved && (input.showId === null || saved.showId === input.showId)
      && saved.expiresAt > input.now && saved.expiresAt <= expiresAt && saved.id
    ? saved.id
    : input.createId();

  if (id !== saved?.id) {
    try {
      input.store.write(
        JSON.stringify({ expiresAt, id, showId: input.showId ?? saved?.showId ?? "" }),
        // From the same clock as the expiry, so the cookie cannot outlive the id. Rounded up,
        // because a cookie that expired a second early would drop the id mid-Show.
        Math.ceil((expiresAt - input.now) / 1_000),
      );
    } catch {
      // A refused store leaves the id in memory for this page, fragmenting the funnel the way a
      // memory-only session does. Throwing instead would disable analytics for the whole visit.
    }
  }

  return {
    ...common,
    bootstrap: { distinctID: id },
    // Trovie persists only the bounded anonymous id above. Keeping PostHog itself in memory
    // prevents its session-attribution cache from retaining routed search text.
    persistence: "memory" as const,
  };
}
